/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceDescription } from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Base, IEditorResource_Readme } from "../../../resources/ResourceTypes";
import { IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { ViewService } from "../../../view/ViewService";
import { README_EDITOR_VIEW_MODE } from "../types";
import { ReadmeModel } from "../Common/ReadmeModel";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { ReadmeEditorLayoutConfig } from "./ReadmeEditorLayout";
import { CommandsService } from "../../../commands/CommandsService";

interface IReadmeEditorViewParams {
	resourceId: string;
}

export class ReadmeEditorView implements IView<IReadmeEditorViewParams>, IViewEditable {
	/* Services */
	private resourceSvc: ResourceService;
	private viewSvc: ViewService;
	private commandsSvc: CommandsService;

	/* Resource */
	private resourceDescriptor: IResourceDescription;
	private resource: IEditorResource_Readme;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/** Editor mode */
	private editorMode: README_EDITOR_VIEW_MODE = README_EDITOR_VIEW_MODE.VIEW;

	/** If resource was modified */
	public isModified: boolean;

	/** If the view is being saved */
	private saveInProgress = false;

	/** Readme model */
	private readmeModel: ReadmeModel;

	/** Layout state */
	private readonly layout = new LayoutState(ReadmeEditorLayoutConfig);

	/* Event handlers */
	private handleModelChange: () => void;
	private handleResourceChange: () => void;
	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	>;

	/** Emitted when editor mode change */
	public onModeChange = createEventEmitter();

	/** Emitted when content changes */
	public onContentChange = createEventEmitter<string>();

	/**
	 * View constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.resourceSvc = dc.get("resourceService");
		this.viewSvc = dc.get("viewService");
		this.commandsSvc = dc.get("commandsService");
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IReadmeEditorViewParams, viewId: string): Promise<void> {
		this.resourceDescriptor = this.resourceSvc.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceSvc.getBlueprintById(params.resourceId)) as IEditorResource_Readme;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		this.readmeModel = new ReadmeModel();
		await this.readmeModel.loadDocument(this.resource.content);
		this.readmeModel.selectFirstPage();

		onEvent(
			this.readmeModel.onModelChange,
			(this.handleModelChange = () => {
				this.setIsModified(true, true);
			})
		);

		onEvent(
			this.resource.onChange,
			(this.handleResourceChange = () => {
				this.resource = this.resourceSvc.getResourceById(params.resourceId) as IEditorResource_Readme;
				this.resourceDescriptor = this.resource.descriptor;
				this.emitViewInfoChanged();
				emitEvent(this.onResourceInfoChanged);
			})
		);

		onEvent(
			this.resource.onDelete,
			(this.handleResourceDelete = ({ deletedByLocalAction }) => {
				if (deletedByLocalAction || !this.isModified) {
					this.isModified = false;
					this.viewSvc.close(viewId);
				}
			})
		);
	}

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getMode(): README_EDITOR_VIEW_MODE {
		return this.editorMode;
	}

	public setMode(mode: README_EDITOR_VIEW_MODE): void {
		this.editorMode = mode;
		emitEvent(this.onModeChange);
	}

	public getResource(): IEditorResource_Readme {
		return this.resource;
	}

	public getResourceDescriptor(): IResourceDescription {
		return this.resourceDescriptor;
	}

	public getModel(): ReadmeModel {
		return this.readmeModel;
	}

	public getResourceParentList(): IResourceDescription[] {
		return this.resourceSvc.getResourceParentList(this.resource.id);
	}

	/**
	 * Opens a blueprint by its path
	 *
	 * @param path Path to the blueprint (without .yaml extension)
	 */
	public openBlueprintByPath(path: string): void {
		const resource = this.resourceSvc.getResourceDescriptorByPath(path);

		if (resource) {
			this.commandsSvc.execute("resource.open", resource.id).catch((err) => {
				console.error(`Failed to open blueprint at path "${path}":`, err);
			});
		} else {
			console.warn(`Blueprint not found at path "${path}"`);
		}
	}

	public canUndo(): boolean {
		return this.readmeModel.undoRedoManager.canUndo();
	}

	public canRedo(): boolean {
		return this.readmeModel.undoRedoManager.canRedo();
	}

	public undo(): void {
		this.readmeModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo(): void {
		this.readmeModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor.name,
			icon: "mdi/file-document",
			isModified: this.isModified,
			canClose: true
		};
	}

	public emitViewInfoChanged(): void {
		emitEvent(this.onViewInfoChanged);
	}

	public setIsModified(modified: boolean, emitInfoChange: boolean): void {
		this.isModified = modified;

		if (emitInfoChange) {
			this.emitViewInfoChanged();
		}
	}

	public canSave(): boolean {
		return this.isModified && !this.saveInProgress;
	}

	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	public async save(): Promise<void> {
		if (!this.canSave()) {
			return;
		}

		this.saveInProgress = true;
		this.emitViewInfoChanged();

		try {
			const content = this.readmeModel.exportDocumentAsYAML();
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);
			this.setIsModified(false, true);
		} finally {
			this.saveInProgress = false;
			this.emitViewInfoChanged();
		}
	}

	public dispose(): void {
		if (this.readmeModel) {
			offEvent(this.readmeModel.onModelChange, this.handleModelChange);
			this.readmeModel.dispose();
		}

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}
	}

	public destroy(): void {
		this.dispose();
	}
}
